/**
 * מרכז התראות (spec §4.7): filters הכול/לא נקראו/חיובים/חריגות/החזרות/חיבורים, grouping into
 * היום · השבוע · קודם, mark-as-read and dismiss (never a hard delete — `dismissedAt`).
 */

import type { Alert, AlertFilter, AlertType, AlertsResponse, SummaryResponse } from '../../../shared-types.ts';
import { relTime } from '../format.ts';
import { ALERT_ICON, he } from '../i18n/he.ts';
import { addDays } from '../dates.ts';
import { Icon } from '../components/Icon.tsx';
import { EmptyState } from '../components/ui.tsx';
import { CategoryIcon as KitCategoryIcon } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';
import { alertTarget } from '../components/Shell.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';

const FILTERS: AlertFilter[] = ['all', 'unread', 'cards', 'over', 'returns', 'conn'];

export function AlertsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, toast } = useApp();
  const { route, navigate } = useRouter();
  /**
   * The filter lives in `?filter=` (router.ts documents `/alerts ?filter`, ARCHITECTURE §10 "deep
   * links keep the mockup's query params"). It used to be component state, so `/alerts?filter=unread`
   * opened on "הכול" and a chosen filter could not be shared, bookmarked or survive a back/forward.
   * `all` is the default and is left out of the URL.
   */
  const raw = route.query.filter;
  const filter: AlertFilter = FILTERS.includes(raw as AlertFilter) ? (raw as AlertFilter) : 'all';
  const setFilter = (next: AlertFilter): void => {
    navigate(buildPath('alerts', null, { filter: next === 'all' ? null : next }), { replace: true });
  };
  const query = useQuery<AlertsResponse>(cacheKey('alerts', { filter }), () => api.alerts({ filter }), { scopes: ['alerts', 'summary'] });
  const data = query.data;
  const asOf = summary?.asOf ?? new Date().toISOString().slice(0, 10);
  const weekAgo = addDays(asOf, -7);

  const groups: Array<[string, Alert[]]> = [
    [he.alerts.groups.today, []],
    [he.alerts.groups.week, []],
    [he.alerts.groups.before, []],
  ];
  for (const alert of data?.items ?? []) {
    const day = alert.createdAt.slice(0, 10);
    const bucket = day === asOf ? groups[0] : day >= weekAgo ? groups[1] : groups[2];
    bucket?.[1].push(alert);
  }

  return (
    <div className="screen">
      <div className="alerts-wrap">
        <div className="row between wrap">
          <div className="toggle-group" role="group" aria-label={he.alerts.filterLabel}>
            {FILTERS.map((key) => (
              <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}>
                {he.alerts.filters[key]}
              </button>
            ))}
          </div>
          {(data?.unreadCount ?? 0) > 0 ? (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() =>
                api.readAllAlerts({ filter }).then(() => {
                  query.refetch();
                  toast(he.alerts.readAllDone);
                })
              }
            >
              <Icon name="checkCircle" className="xs" />
              {he.alerts.readAll}
            </button>
          ) : null}
        </div>

        {!data ? (
          <div className="sk" style={{ height: '320px' }} />
        ) : !data.items.length ? (
          <EmptyState illustration="noAlerts" title={he.empty.noAlerts.title} body={he.empty.noAlerts.body} />
        ) : (
          groups
            .filter(([, items]) => items.length)
            .map(([label, items]) => (
              <section className="sec" style={{ border: 0, gap: '4px' }} aria-label={label} key={label}>
                <h2 className="meta" style={{ fontSize: '12px', fontWeight: 600 }}>
                  {label}
                </h2>
                <div>
                  {items.map((alert) => (
                    <AlertRow key={alert.id} alert={alert} onChanged={() => query.refetch()} />
                  ))}
                </div>
              </section>
            ))
        )}
      </div>
    </div>
  );
}

/**
 * REDESIGN §7b — the drawn glyph for an alert type. The kit carries a glyph for most of them;
 * `data_stale` / `refresh_failed` / `connection_expired` / `new_account` have no drawn
 * equivalent yet, so they keep the app's own `Icon.tsx` mark, which is the same 24 viewBox and
 * the same 1.75 stroke and sits identically inside the circle.
 */
const ALERT_GLYPH: Partial<Record<AlertType, GlyphName>> = {
  card_due_soon: 'card',
  coverage_low: 'coins',
  category_over_baseline: 'percent',
  large_transaction: 'banknote',
  returned_payment: 'undo',
};

function AlertGlyph({ type }: { type: AlertType }) {
  const glyph = ALERT_GLYPH[type];
  return glyph ? <KitCategoryIcon glyph={glyph} size={18} /> : <Icon name={ALERT_ICON[type]} size={18} />;
}

function AlertRow({ alert, onChanged }: { alert: Alert; onChanged: () => void }) {
  const { api, toast } = useApp();
  const read = Boolean(alert.readAt);
  return (
    <div className={`alrow${read ? ' read' : ''}`}>
      <span className={`aic ${alert.severity}`} aria-hidden="true">
        <AlertGlyph type={alert.type} />
      </span>
      <Link
        className="grow rowlink"
        to={alertTarget(alert)}
        style={{ minWidth: 0 }}
        onClick={() => {
          if (!read) void api.updateAlert(alert.id, { read: true }).then(onChanged);
        }}
      >
        <span className="at" style={{ display: 'block' }}>
          {!read ? <span className="sr-only">{he.alerts.unreadPrefix}</span> : null}
          {alert.title}
        </span>
        <span className="ab" style={{ display: 'block' }}>
          {alert.body}
        </span>
        <span className="am">
          {relTime(alert.createdAt)}
          {read ? ` · ${he.alerts.read}` : ''}
        </span>
      </Link>
      {!read ? <span className="unread" aria-hidden="true" /> : null}
      <span className="rowacts">
        {!read ? (
          <button type="button" className="btn-icon" aria-label={he.alerts.markRead} onClick={() => api.updateAlert(alert.id, { read: true }).then(onChanged)}>
            <Icon name="check" className="s" />
          </button>
        ) : null}
        <button
          type="button"
          className="btn-icon"
          aria-label={he.alerts.delete}
          onClick={() =>
            api.updateAlert(alert.id, { dismissed: true }).then(() => {
              onChanged();
              toast(he.alerts.deleted, {
                action: { label: he.app.undo, onClick: () => void api.updateAlert(alert.id, { dismissed: false }).then(onChanged) },
              });
            })
          }
        >
          <Icon name="trash" className="s" />
        </button>
      </span>
      <Icon name="chevL" className="s chev" />
    </div>
  );
}
